import { PERSONNEL_STATUS_FILTER_OPTIONS, PersonnelStatusFilterOption } from "enums/PersonnelEnums";
import { useQueryParam } from "hooks/useQueryParam";
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
	getFiltersFromConfig,
	loadGridConfig,
	saveGridConfig,
	updateFiltersInConfig
} from "utils/gridConfig";

const CONFIG_PATH = ["personnelAndFleet", "personnel"];

type PersonnelFilters = { status: string };

export function usePersonnelQueryParams() {
	const [, setSearchParams] = useSearchParams();

	// Local state: seeded from URL if present on mount, otherwise from localStorage.
	// Kept separate from the URL param so fleet's shared "status" param can't interfere.
	const [personnelStatus, setPersonnelStatusState] = useState<PersonnelStatusFilterOption>(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.has("status")) {
			return params.get("status") as PersonnelStatusFilterOption;
		}
		const saved = getFiltersFromConfig<PersonnelFilters>(loadGridConfig(), CONFIG_PATH);
		return (saved?.status as PersonnelStatusFilterOption) ?? PERSONNEL_STATUS_FILTER_OPTIONS.ACTIVE;
	});

	const [, setPersonnelStatusParam] = useQueryParam<PersonnelStatusFilterOption>(
		"status",
		PERSONNEL_STATUS_FILTER_OPTIONS.ACTIVE
	);
	const setPersonnelStatus = useCallback(
		(status: PersonnelStatusFilterOption) => {
			setPersonnelStatusState(status);
			setPersonnelStatusParam(status);
			saveGridConfig(updateFiltersInConfig(loadGridConfig(), CONFIG_PATH, { status }));
		},
		[setPersonnelStatusParam]
	);
	const [searchFilter, setSearchFilter] = useQueryParam<string>("search", "");

	const applyFilters = useCallback(
		(status: PersonnelStatusFilterOption, search: string) => {
			setPersonnelStatusState(status);
			saveGridConfig(updateFiltersInConfig(loadGridConfig(), CONFIG_PATH, { status }));
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (status === PERSONNEL_STATUS_FILTER_OPTIONS.ACTIVE) {
						next.delete("status");
					} else {
						next.set("status", status);
					}
					if (!search) {
						next.delete("search");
					} else {
						next.set("search", search);
					}
					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	return { personnelStatus, setPersonnelStatus, searchFilter, setSearchFilter, applyFilters };
}
